import { OktaContext } from '@okta/okta-react';
import { ReactNode } from 'react';

type CypressSecurityMockProps = {
    children: ReactNode;
};

/**
 * Mock Security component for Cypress tests.
 * Bypasses real Okta authentication and provides a mock authenticated state.
 */
export const CypressSecurityMock = ({ children }: CypressSecurityMockProps): JSX.Element => {
    const mockUser = (window as any).cypressMockUser ?? {
        sub: 'mock-sub',
        email: 'test@test.com',
        name: 'Test User',
    };

    const now = Math.floor(Date.now() / 1000);
    const expiresAt = now + 3600;

    const mockAuthState = {
        isAuthenticated: true,
        isPending: false,
        isLoading: false,
        idToken: {
            idToken: 'cypress-mock-id-token',
            claims: mockUser,
            expiresAt,
            scopes: ['openid', 'profile', 'email'],
            issuer: 'https://mock.okta.local/oauth2/default',
            clientId: 'mock-client-id',
            authorizeUrl: 'https://mock.okta.local/oauth2/default/v1/authorize',
        },
        accessToken: {
            accessToken: 'cypress-mock-access-token',
            claims: { sub: mockUser.sub },
            expiresAt,
            scopes: ['openid', 'profile', 'email'],
            tokenType: 'Bearer',
            authorizeUrl: 'https://mock.okta.local/oauth2/default/v1/authorize',
            userinfoUrl: 'https://mock.okta.local/oauth2/default/v1/userinfo',
        },
        error: null,
    };

    const mockOktaAuth = {
        isAuthenticated: (): Promise<boolean> => Promise.resolve(true),
        getUser: (): Promise<any> => Promise.resolve(mockUser),
        authStateManager: {
            getAuthState: (): any => mockAuthState,
            updateAuthState: (): Promise<any> => Promise.resolve(mockAuthState),
            subscribe: (handler: (authState: any) => void): (() => void) => {
                // Call handler on next microtask to mimic async behavior of real Okta SDK
                // This allows the subscribing component to complete its current render cycle
                queueMicrotask(() => handler(mockAuthState));
                // Return unsubscribe function
                return () => {
                    // No-op unsubscribe
                };
            },
            unsubscribe: (): void => {
                // No-op unsubscribe for cleanup
            },
        },
        tokenManager: {
            get: (key: string): Promise<any> => {
                if (key === 'idToken') return Promise.resolve(mockAuthState.idToken);
                if (key === 'accessToken') return Promise.resolve(mockAuthState.accessToken);
                return Promise.resolve(null);
            },
        },
        signOut: (): Promise<void> => Promise.resolve(),
    };

    const contextValue = {
        oktaAuth: mockOktaAuth as any,
        authState: mockAuthState as any,
    };

    return <OktaContext.Provider value={contextValue}>{children}</OktaContext.Provider>;
};
